'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Phone, Mail, MessageCircle } from 'lucide-react';
import config from '@/lib/config';

const Footer2 = () => {
  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/menu', label: 'Our Menu' },
    { path: '/cart', label: 'My Cart' },
    { path: '/contact', label: 'Contact Us' },
  ];

  const socialLinks = [
    {
      name: 'Instagram',
      url: config.socials.instagram.url,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            d="M7.75 2h8.5A5.75 5.75 0 0122 7.75v8.5A5.75 5.75 0 0116.25 22h-8.5A5.75 5.75 0 012 16.25v-8.5A5.75 5.75 0 017.75 2z"
          />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 8h.01" />
          <circle cx="12" cy="12" r="3.5" />
        </svg>
      ),
    },
    {
      name: 'TikTok',
      url: config.socials.tiktok.url,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" width="600" height="600" viewBox="0 0 600 600">
          <title>TikTok</title>
          <g id="BACKGROUND">
            <circle cx="300" cy="300" r="300" />
          </g>
          <g id="ICONS">
            <path
              d="M260.18,257.1V240.82a123.43,123.43,0,0,0-87.44,223.47A123.46,123.46,0,0,1,260.18,257.1Z"
              fill="#25f4ee"
            />
            <path
              d="M263.09,436.94c30.4,0,55.54-24.52,56.67-54.64L320,113.71l48.7,0-.07-.41a92.76,92.76,0,0,1-1.59-17H300l-.11,269a56.29,56.29,0,0,1-82.48,47.81C227.62,427.29,244.27,436.94,263.09,436.94Z"
              fill="#25f4ee"
            />
            <path
              d="M460.44,205.42V189.66a92.77,92.77,0,0,1-50.85-15.12C422.63,189.52,440.34,201.12,460.44,205.42Z"
              fill="#25f4ee"
            />
            <path d="M409.59,174.54a92.9,92.9,0,0,1-23-61.22H368.34A94,94,0,0,0,409.59,174.54Z" fill="#fe2c55" />
            <path
              d="M243.53,306.21c-31.11,0-56.79,25.83-56.79,56.93,0,21.67,12.66,40.49,30.62,49.94a56.32,56.32,0,0,1,45.73-89.32,55.25,55.25,0,0,1,16.65,2.61V257.85a123.46,123.46,0,0,0-16.65-1.21c-1,0-1.94.05-2.91.08h-.26l-.18,51.86A52.61,52.61,0,0,0,243.53,306.21Z"
              fill="#fe2c55"
            />
            <path
              d="M460.44,204.62l-.32-.06v51.82A160.17,160.17,0,0,1,366.65,226l0,137.11A123.51,123.51,0,0,1,172.74,464.29a123.45,123.45,0,0,0,213.89-84.11V243.78a159.56,159.56,0,0,0,93.37,30V206.7A93.39,93.39,0,0,1,460.44,204.62Z"
              fill="#fe2c55"
            />
            <path
              d="M367.06,363.14V226.75a159.61,159.61,0,0,0,93.38,30V204.62a93.45,93.45,0,0,1-50.85-30.08,93.51,93.51,0,0,1-40.94-61.22H319.51l-.11,269a56.31,56.31,0,0,1-102,30.78,56.39,56.39,0,0,1,26.17-106.35,55.56,55.56,0,0,1,16.65,2.6V256.72C193.41,258.28,139.56,313,139.56,380.18a123.05,123.05,0,0,0,33.18,84.11A123.43,123.43,0,0,0,367.06,363.14Z"
              fill="#fff"
            />
          </g>
        </svg>
      ),
    },
  ];

  const contactInfo = [
    { icon: Mail, text: config.contact.email, link: `mailto:${config.contact.email}` },
    { icon: Phone, text: config.contact.phone, link: `tel:${config.contact.phoneRaw}` },
    { icon: MessageCircle, text: 'WhatsApp', link: `https://wa.me/${config.contact.phoneRaw}` },
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-950 via-gray-800 to-gray-950 text-white  overflow-hidden print:hidden">
      {/* Floating elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          animate={{ y: [0, -10, 0], rotate: [0, 3, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-16 left-1/4 w-1.5 h-1.5 bg-orange-600/30 rounded-full"
        />
        <motion.div
          animate={{ y: [0, 12, 0], rotate: [0, -3, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-24 right-1/3 w-2 h-2 bg-orange-600/30 rounded-full"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="grid lg:grid-cols-2 gap-10">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-5">
            <Link href="/" className="inline-block group">
              <h2 className="text-3xl font-bold text-orange-500">
                Jayouz{' '}
                <span className="italic font-light bg-gradient-to-r from-white to-orange-200 bg-clip-text text-transparent">
                  Cuisine
                </span>
              </h2>
            </Link>
            <p className="text-lg text-gray-400 max-w-md leading-relaxed">
              Homemade. Handcrafted. Delivered with love. Experience the warmth of authentic cuisine made fresh daily.
            </p>
            {/*            <div className="flex items-center gap-3 text-orange-600">
              <Heart className="w-5 h-5" />
              <span className="text-base">Handcrafted with love since 2025</span>
            </div>*/}
          </motion.div>
        </div>

        {/* Quick Links Row */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="grid gap-6 lg:gap-10 mt-8">
          <div className="flex flex-col justify-between sm:flex-row gap-6 lg:gap-8">
            {/* Explore */}
            <div className="space-y-3">
              <div className="text-sm tracking-[0.2em] font-semibold text-white uppercase">Explore</div>
              <div className="grid gap-2">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}>
                    <Link href={link.path} className="text-base text-gray-300 hover:text-orange-500 transition">
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Connect */}
            <div className="space-y-3">
              <div className="text-sm tracking-[0.2em] font-semibold text-white uppercase">Connect</div>
              <div className="space-y-2">
                {contactInfo.map((item, i) => (
                  <a
                    key={i}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-300 hover:text-orange-500 transition">
                    <div className="w-8 h-8 bg-orange-600/10 rounded-full flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-orange-500" />
                    </div>
                    <span className="text-base">{item.text}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Follow */}
            <div className="space-y-3">
              <div className="text-sm tracking-[0.2em] font-semibold text-white uppercase">Follow</div>
              <div className="space-y-2">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    whileHover={{ x: 4 }}
                    transition={{ duration: 0.2 }}
                    className="flex items-center gap-3 text-gray-300 hover:text-orange-500 transition">
                    <div className="w-8 h-8 bg-orange-600/10 rounded-full flex items-center justify-center">
                      {social.icon}
                    </div>
                    <span className="text-base">{social.name}</span>
                  </motion.a>
                ))}
              </div>
            </div>

            {/* HOURS */}
            <div className="space-y-3">
              <div className="text-sm tracking-[0.2em] font-semibold text-white uppercase">Hours</div>
              <div className="space-y-2">
                <div className="flex justify-between items-center gap-4 text-gray-300">
                  <span className="text-base">Mon - Friday</span>
                  <span className="text-base font-light">9AM - 9PM</span>
                </div>
                <div className="flex justify-between items-center gap-4 text-gray-300">
                  <span className="text-base">Sat - Sunday</span>
                  <span className="text-base font-light">10AM - 10PM</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="border-t border-gray-800 mt-10 pt-6 text-sm text-gray-500 flex flex-col sm:flex-row justify-between items-center gap-3">
          <p>© 2025 Jayouz Cuisine. All rights reserved.</p>
          <a
            href="https://toxiostek.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-300 transition duration-300">
            [ Developed by Toxios Tek ]
          </a>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer2;
